/**
 * @fileoverview Loads the Task Tracker matrix of the project being viewed,
 * optionally filtered to one student, and keeps polling while the backend
 * is still preparing a newly added participant.
 */

import { useCallback, useEffect, useState } from 'react'
import { fetchTaskTrackMatrix } from '../../../TaskTracker/service/taskTrackerService'
import { useProcessingPoll } from '../../../TaskTracker/hooks/useProcessingPoll'
import { decorateMatrixForReview } from '../../../TaskTracker/teacherUpload'
import type { TaskTrackMatrix } from '../../../TaskTracker/types'

export const ALL_STUDENTS = 'all'

interface UseProjectMatrixArgs {
  projectId: string | undefined
  selectedStudentId: string
  onLoadError: () => void
}

export function useProjectMatrix({ projectId, selectedStudentId, onLoadError }: UseProjectMatrixArgs) {
  const [matrix, setMatrix] = useState<TaskTrackMatrix | null>(null)
  const [loading, setLoading] = useState(true)
  const studentId = selectedStudentId === ALL_STUDENTS ? undefined : selectedStudentId

  const fetchMatrix = useCallback(async () => {
    if (!projectId) return null
    return decorateMatrixForReview(await fetchTaskTrackMatrix(projectId, studentId))
  }, [projectId, studentId])

  // `onLoadError` only resolves a toast message, so the latest reference is captured by closure.
  const load = useCallback(async () => {
    setLoading(true)
    try {
      setMatrix(await fetchMatrix())
    } catch {
      setMatrix(null)
      onLoadError()
    } finally {
      setLoading(false)
    }
  }, [fetchMatrix]) // eslint-disable-line react-hooks/exhaustive-deps

  // Silent refetch: keeps the current table visible while a write or the processing poll lands.
  const reloadSilent = useCallback(async () => {
    try {
      setMatrix(await fetchMatrix())
    } catch {
      // keep the current data; the next poll (or a manual refresh) retries
    }
  }, [fetchMatrix])

  useEffect(() => {
    void load()
  }, [load])

  useProcessingPoll(Boolean(matrix?.processing), reloadSilent)

  return { matrix, loading, reload: load, reloadSilent }
}
